import { useListingMatchVersions } from "./use-listing-match-versions";
import { useMutation, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ListingMatchSetup } from "./ListingMatchSetup";
import { MatchAutoMatchSetting } from "@/components/match/auto-match/MatchAutoMatchSetting";
import { Button } from "@/components/design/Button";
import { useState } from "react";
import { Select } from "@/components/design/Select";
import { buttonClasses } from "@/components/design/button-classes";
import { SavedCandidateRanking } from "@/components/match/SavedCandidateRanking";
import { AutoMatchRunsBox } from "@/components/match/auto-match/AutoMatchRunsBox";
import { RetryListingMatchVersionDocument, MatchResultListDocument, SortOrder } from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import { userErrorMessage } from "@/lib/user-error";
import type { LinkedMatch } from "@/pages/project/use-linked-matches";

export function ListingRanking({ matches, listingId, canManage = false, onOpenRequirements, beforePrepare, versionsQuery, canReadApplications = false }: {
  canReadApplications?: boolean;
  versionsQuery?: ReturnType<typeof useListingMatchVersions>;
  beforePrepare?: () => Promise<void>;
  matches: readonly LinkedMatch[]; listingId?: string; canManage?: boolean; onOpenRequirements?: () => void;
}) {
  const intl = useIntl();
  const [params, setParams] = useSearchParams();
  const ownVersions = useListingMatchVersions(versionsQuery ? undefined : listingId);
  const versions = versionsQuery ?? ownVersions;
  const [retry, { loading: retrying }] = useMutation(RetryListingMatchVersionDocument);
  const [error, setError] = useState<string | null>(null);
  const allVersions = (versions.data?.listing?.matches?.filter((m) => m.listingVersion) ?? [])
    .sort((a, b) => (b.listingVersion?.number ?? 0) - (a.listingVersion?.number ?? 0));
  const running = allVersions.find((m) => m.listingVersion?.state === "RUNNING");
  const failed = allVersions.find((m) => m.listingVersion?.state === "FAILED");
  const available = allVersions.filter((m) => m.listingVersion?.state === "ACTIVE" || m.listingVersion?.state === "ARCHIVED");
  const options = available.length ? available : matches;
  const active = available.find((m) => m.listingVersion?.state === "ACTIVE");
  const pending = running ?? (failed && (!active || (failed.listingVersion?.number ?? 0) > (active.listingVersion?.number ?? 0)) ? failed : undefined);
  async function retryVersion(id: string) {
    setError(null);
    try { await retry({ variables: { input: { matchId: id } } }); await versions.refetch(); }
    catch (e) { setError(userErrorMessage(intl, e)); }
  }
  const chosen = options.find((match) => match.id === params.get("ranking"));
  const match = chosen ?? (active ?? (options.length === 1 ? options[0] : undefined));
  const resultQuery = useLatestRanking(match?.id, !!match?.autoMatch || !!running);
  const selectedVersion = allVersions.find((version) => version.id === match?.id)?.listingVersion;
  const versionControl = options.length > 1 ? (
    <div className="w-56 max-w-full shrink-0">
      <label htmlFor="listing-ranking-select" className="sr-only">
        <FormattedMessage id="share.listings.ranking.select" />
      </label>
      <Select id="listing-ranking-select" value={match?.id} required variant="plain"
        placeholder={intl.formatMessage({ id: "share.listings.ranking.select" })}
        options={options.map((m) => {
          const version = allVersions.find((v) => v.id === m.id)?.listingVersion;
          return { value: m.id, label: version ? intl.formatMessage({ id: version.state === "ACTIVE"
            ? "share.listings.versions.current" : "share.listings.versions.history" }, { number: version.number })
            : `${m.name ?? ""} · ${formatDateTime(m.createdAt, intl.locale)}` };
        })}
        onChange={(id) => setParams((previous) => {
          const next = new URLSearchParams(previous); next.set("ranking", id); return next;
        }, { replace: true })} />
    </div>
  ) : match ? <span className="text-smycles-body font-medium">
    <FormattedMessage id={selectedVersion?.state === "ARCHIVED" ? "share.listings.versions.history" : "share.listings.versions.current"}
      values={{ number: selectedVersion?.number ?? 1 }} />
  </span> : null;
  return (
    <section className="space-y-4">
      {listingId && match && (!active || active.id === match.id) ? <MatchAutoMatchSetting matchId={match.id} showLinkedListings={false} /> : null}
      <div className="flex flex-wrap items-center gap-3">
        <h3 id="listing-ranking-heading" className="text-smycles-title"><FormattedMessage id="share.listings.ranking.title" /></h3>
        {match ? <Link to={`/match/${match.id}?step=${resultQuery.data?.matchResults.nodes[0] ? `result&result=${resultQuery.data.matchResults.nodes[0].id}` : "challenge"}`}
          className={buttonClasses({ variant: "secondary", size: "compact" })}>
          <FormattedMessage id="share.listings.ranking.openMatch" />
        </Link> : null}
      </div>
      {versions.error || error ? <p role="alert">{error ?? userErrorMessage(intl, versions.error)}</p> : null}
      {pending ? <div role="status" className="flex flex-wrap items-center gap-3">
        <p className="smycles-field-meta"><FormattedMessage id={pending.listingVersion?.state === "RUNNING"
          ? "share.listings.versions.running" : "share.listings.versions.failed"} values={{ number: pending.listingVersion?.number }} /></p>
        {canManage && pending.listingVersion?.canRetry ? <Button variant="secondary" size="compact" disabled={retrying}
          onClick={() => void retryVersion(pending.id)}><FormattedMessage id="share.listings.versions.retry" /></Button> : null}
      </div> : null}
      {options.length > 0 ? <div className="border-b border-ink/10 pb-4">
        {match && listingId ? <ListingMatchSetup listingId={listingId} matchId={match.id} canManage={canManage}
          versionControl={versionControl} historical={!!active && active.id !== match.id} pending={!!running}
          beforePrepare={beforePrepare}
          onOpenRequirements={onOpenRequirements} onStarted={async () => {
            setParams((previous) => {
              const next = new URLSearchParams(previous); next.delete("ranking"); return next;
            }, { replace: true });
            await versions.refetch();
          }} /> : versionControl}
      </div> : null}
      {match ? <>
        <CurrentRanking key={match.id} listingId={canReadApplications ? listingId : undefined} match={match} query={resultQuery} compact={!!listingId} historical={!!active && active.id !== match.id} />
      </> : null}
    </section>
  );
}

function useLatestRanking(matchId?: string, watching = false) {
  return useQuery(MatchResultListDocument, {
    variables: { input: { first: 1, where: { matchId: { eq: matchId ?? "" }, kept: { eq: true } }, order: { createdAt: SortOrder.Desc } } },
    skip: !matchId, fetchPolicy: "cache-and-network", pollInterval: watching ? 20_000 : 0,
  });
}

function CurrentRanking({ match, compact, historical, query, listingId }: {
  listingId?: string;
  match: Pick<LinkedMatch, "id" | "name" | "autoMatch">; compact?: boolean; historical?: boolean;
  query: ReturnType<typeof useLatestRanking>;
}) {
  const matchId = match.id;
  const intl = useIntl();
  const navigate = useNavigate();
  const latest = query.data?.matchResults.nodes[0];
  return (
    <div className="space-y-4">
      {!compact ? <div className="space-y-3">
        <p className="text-smycles-body font-medium">{match.name}</p>
        <p className="text-smycles-meta text-ink-muted"><FormattedMessage id="share.listings.ranking.poolHint" /></p>
        <div className="flex flex-wrap gap-2">
          <Link id="listing-match-candidates" to={`/match/${matchId}?step=people`}
            className={buttonClasses({ variant: "primary", size: "compact" })}>
            <FormattedMessage id="share.listings.ranking.continue" />
          </Link>
          <Link id="listing-match-requirements" to={`/match/${matchId}?step=challenge`}
            className={buttonClasses({ variant: "secondary", size: "compact" })}>
            <FormattedMessage id="share.listings.ranking.requirements" />
          </Link>
        </div>
      </div> : null}
      {query.error ? <p role="alert">{userErrorMessage(intl, query.error)}</p> :
        query.loading && !query.data ? <p aria-busy="true"><FormattedMessage id="common.loading" /></p> :
          latest ? <SavedCandidateRanking key={latest.id} matchId={matchId} resultId={latest.id} listingId={listingId} /> :
            <p className="text-smycles-body text-ink-muted"><FormattedMessage id="share.listings.ranking.waiting" /></p>}
      {!historical ? <AutoMatchRunsBox matchId={matchId} latestSavedResultAt={latest?.createdAt ?? null}
        onRematch={compact ? undefined : () => void navigate(`/match/${matchId}?step=result`)} /> : null}
    </div>
  );
}
